/**
 * Bottom-right toast that surfaces PWA lifecycle events:
 *
 *   - `needRefresh` → "新版本可用" banner with a sticky "重启应用" button.
 *     The toast is intentionally NON-dismissable from the X button alone
 *     (X just hides it for this session) because the new service worker
 *     is already installed and will activate on the next full reload —
 *     letting the user "snooze" forever would leave them on an old SW.
 *
 *   - `offlineReady` → 5s success toast, "已可离线使用". One-time.
 *
 * Sits in the same fixed bottom-right region as TransportToasts / Autoswitch.
 * z-index 50 matches the existing toast layer; stacking order is in DOM
 * registration order (later events render below earlier ones).
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, RefreshCw, X } from 'lucide-react';
import clsx from 'clsx';
import { applyPwaUpdate, subscribePwa } from '@/lib/pwa';

interface ToastState {
  needRefresh: boolean;
  offlineReady: boolean;
  /** Set when applyPwaUpdate() is in flight so we can disable the button. */
  refreshing: boolean;
}

const INITIAL: ToastState = { needRefresh: false, offlineReady: false, refreshing: false };
const OFFLINE_DISMISS_MS = 5_000;

export function UpdateToast(): JSX.Element | null {
  const [state, setState] = useState<ToastState>(INITIAL);

  useEffect(() => {
    const unsub = subscribePwa((ev) => {
      if (ev.type === 'needRefresh') {
        setState((s) => ({ ...s, needRefresh: true }));
      } else if (ev.type === 'offlineReady') {
        setState((s) => ({ ...s, offlineReady: true }));
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!state.offlineReady) return;
    const t = window.setTimeout(
      () => setState((s) => ({ ...s, offlineReady: false })),
      OFFLINE_DISMISS_MS,
    );
    return () => window.clearTimeout(t);
  }, [state.offlineReady]);

  if (!state.needRefresh && !state.offlineReady) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[340px] flex-col gap-2">
      {state.needRefresh ? (
        <NeedRefreshRow
          refreshing={state.refreshing}
          onReload={async () => {
            setState((s) => ({ ...s, refreshing: true }));
            await applyPwaUpdate();
            // applyPwaUpdate triggers a full reload; if it returns we
            // assume the SW skipped waiting silently — clear the flag.
            setState((s) => ({ ...s, refreshing: false }));
          }}
          onDismiss={() => setState((s) => ({ ...s, needRefresh: false }))}
        />
      ) : null}
      {state.offlineReady ? (
        <OfflineReadyRow
          onDismiss={() => setState((s) => ({ ...s, offlineReady: false }))}
        />
      ) : null}
    </div>
  );
}

interface NeedRefreshRowProps {
  refreshing: boolean;
  onReload: () => void;
  onDismiss: () => void;
}

function NeedRefreshRow({ refreshing, onReload, onDismiss }: NeedRefreshRowProps): JSX.Element {
  return (
    <div
      role="alert"
      className={clsx(
        'pointer-events-auto rounded-lg border border-cyan-700/40 bg-slate-900/95',
        'p-3 text-sm text-slate-100 shadow-lg backdrop-blur',
      )}
    >
      <div className="flex items-start gap-2">
        <RefreshCw className={clsx('mt-0.5 h-4 w-4 shrink-0 text-cyan-400', refreshing && 'animate-spin')} />
        <div className="min-w-0 flex-1">
          <div className="font-medium">新版本可用</div>
          <div className="mt-0.5 text-xs text-slate-300">
            点击「重启应用」加载最新版本，未保存的输入会丢失。
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={onReload}
              disabled={refreshing}
              className={clsx(
                'rounded bg-cyan-500/90 px-2.5 py-1 text-xs font-medium text-slate-900',
                'hover:bg-cyan-400 disabled:cursor-wait disabled:opacity-60',
              )}
            >
              {refreshing ? '更新中…' : '重启应用'}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="rounded px-2 py-1 text-xs text-slate-400 hover:text-slate-200"
            >
              稍后
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="dismiss"
          className="rounded p-1 text-slate-500 hover:text-slate-300"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function OfflineReadyRow({ onDismiss }: { onDismiss: () => void }): JSX.Element {
  return (
    <div
      role="status"
      className="pointer-events-auto flex items-center gap-2 rounded-lg border border-emerald-700/40 bg-slate-900/95 p-3 text-sm text-slate-100 shadow-lg backdrop-blur"
    >
      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
      <div className="min-w-0 flex-1">
        <div className="font-medium">已可离线使用</div>
        <div className="mt-0.5 text-xs text-slate-300">
          下次断网也可以打开本应用（adb 控制仍需 helper 运行）。
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="dismiss"
        className="rounded p-1 text-slate-500 hover:text-slate-300"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
